import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Trash2,
  Loader2,
  User,
  Phone,
  Mail,
  MapPin,
  Home,
  DollarSign,
  FileText,
  FileCheck,
  UserPlus,
  Link2,
  X,
  ExternalLink,
  Plus,
  Search,
  Building,
  CheckCircle2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { simpleContactService, type SimpleContact } from '@/services/contactService-simple';
import { contactLoanService, type ContactLoan, type EncompassLoanResult } from '@/services/contactLoanService';
import { describeContactLoan } from '@/services/contactLoanDisplay';
import { LoanOfficerSelect } from '@/components/contacts/LoanOfficerSelect';
import { supabase } from '@/utils/supabase';
import { DocumentsPanel } from './DocumentsPanel';
import { ContactPreApprovalsPanel } from './ContactPreApprovalsPanel';

// Constants
const TITLE_OPTIONS = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'];
const SUFFIX_OPTIONS = ['Jr.', 'Sr.', 'II', 'III', 'IV', 'PhD', 'MD', 'Esq.'];
const LEAD_SOURCES = ['Direct', 'Referral', 'Website', 'Social Media', 'Advertising', 'Phone Call', 'Walk-in', 'Email Campaign', 'Previous Client'];
const PROPERTY_TYPES = ['Single Family', 'Condo', 'Townhouse', 'Multi-Family', 'Commercial'];
const LOAN_PURPOSES = ['Purchase', 'Refinance', 'Cash-Out Refinance'];

interface ContactDetailPageProps {
  contact: SimpleContact;
  onBack: () => void;
  onSaved?: (contactId: string) => void;
  onDeleted?: (contactId: string) => void;
  onCreatePreApproval?: (prefilledData: {
    borrowerInfo: {
      salutation?: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    contactId: string;
  }) => void;
}

// Contact Form Data
interface ContactFormData {
  title?: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  preferredName?: string;
  suffix?: string;
  email?: string;
  workEmail?: string;
  mobilePhone?: string;
  homePhone?: string;
  workPhone?: string;
  preferredContactMethod?: string;
  linkedinUrl?: string;
  website?: string;
  mailingStreet?: string;
  mailingUnit?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZip?: string;
  mailingCountry?: string;
  originalSource?: string;
  assignedLoanOfficerId?: string;
  optInMarketing?: boolean;
  optInSms?: boolean;
  notes?: string;
}

// Loan Details
interface LoanDetails {
  loanPurpose?: string;
  propertyType?: string;
  estimatedLoanAmount?: number;
  estimatedPropertyValue?: number;
  encompassLoanId?: string;
}

// Co-Borrower Data
interface CoBorrowerData {
  title?: string;
  firstName?: string;
  lastName?: string;
  suffix?: string;
  email?: string;
  mobilePhone?: string;
  linkedContactId?: string;
}

export const ContactDetailPage: React.FC<ContactDetailPageProps> = ({
  contact,
  onBack,
  onSaved,
  onDeleted,
  onCreatePreApproval
}) => {
  const [loading, setLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<'contact' | 'loan' | 'documents' | 'pre-approvals'>('contact');

  // Form state
  const [contactData, setContactData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    mailingCountry: 'USA',
    optInMarketing: true,
    optInSms: false
  });

  const [loanDetails, setLoanDetails] = useState<LoanDetails>({});

  // Co-borrower state
  const [coBorrower, setCoBorrower] = useState<CoBorrowerData | null>(null);
  const [linkedCoBorrowerContact, setLinkedCoBorrowerContact] = useState<SimpleContact | null>(null);
  const [showCoBorrowerForm, setShowCoBorrowerForm] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState<SimpleContact[]>([]);
  const [searchingContacts, setSearchingContacts] = useState(false);

  // Multi-loan state
  const [contactLoans, setContactLoans] = useState<ContactLoan[]>([]);
  const [selectedLoan, setSelectedLoan] = useState<ContactLoan | null>(null);
  const [, setLoadingLoans] = useState(false);
  const [showEncompassSearch, setShowEncompassSearch] = useState(false);
  const [encompassSearchResults, setEncompassSearchResults] = useState<EncompassLoanResult[]>([]);
  const [searchingEncompass, setSearchingEncompass] = useState(false);
  const [showNewPreQualForm, setShowNewPreQualForm] = useState(false);

  // Initialize form with contact data
  useEffect(() => {
    if (contact) {
      setContactData({
        title: contact.title || '',
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        middleName: contact.middleName || '',
        preferredName: contact.preferredName || '',
        suffix: contact.suffix || '',
        email: contact.email || '',
        workEmail: contact.workEmail || '',
        mobilePhone: contact.mobilePhone || '',
        homePhone: contact.homePhone || '',
        workPhone: contact.workPhone || '',
        preferredContactMethod: contact.preferredContactMethod || '',
        linkedinUrl: contact.linkedinUrl || '',
        website: contact.website || '',
        mailingStreet: contact.mailingStreet || '',
        mailingUnit: contact.mailingUnit || '',
        mailingCity: contact.mailingCity || '',
        mailingState: contact.mailingState || '',
        mailingZip: contact.mailingZip || '',
        mailingCountry: contact.mailingCountry || 'USA',
        originalSource: contact.source || '',
        assignedLoanOfficerId: contact.assignedLoanOfficerId || '',
        optInMarketing: contact.optInMarketing ?? true,
        optInSms: contact.optInSms ?? false,
        notes: contact.notes || ''
      });

      // Load loan details if contact has lead data
      loadLoanDetails(contact.id);

      // Load contact loans (multi-loan support)
      loadContactLoans(contact.id);

      // Initialize co-borrower data from spouseInfo
      if (contact.spouseInfo) {
        const spouse = contact.spouseInfo;
        if (spouse.firstName || spouse.lastName || spouse.linkedContactId) {
          setCoBorrower({
            title: spouse.title,
            firstName: spouse.firstName,
            lastName: spouse.lastName,
            suffix: spouse.suffix,
            email: spouse.email,
            mobilePhone: spouse.mobilePhone,
            linkedContactId: spouse.linkedContactId
          });
          setShowCoBorrowerForm(true);

          // If linked to a contact, load that contact's details
          if (spouse.linkedContactId) {
            loadLinkedCoBorrower(spouse.linkedContactId);
          }
        }
      }
    }
  }, [contact]);

  const loadLoanDetails = async (contactId: string) => {
    try {
      const { data: lead } = await supabase
        .from('leads')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (lead) {
        setLoanDetails({
          loanPurpose: lead.loan_purpose,
          propertyType: lead.property_type,
          estimatedLoanAmount: lead.estimated_loan_amount,
          estimatedPropertyValue: lead.estimated_property_value,
          encompassLoanId: lead.encompass_loan_id
        });
      }
    } catch (error) {
      console.error('Failed to load loan details:', error);
    }
  };

  // Load contact loans (multi-loan support)
  const loadContactLoans = async (contactId: string) => {
    setLoadingLoans(true);
    try {
      const loans = await contactLoanService.getLoansForContact(contactId);
      setContactLoans(loans);
      // Select primary loan or first loan
      const primary = loans.find(l => l.isPrimary) || loans[0];
      if (primary) {
        setSelectedLoan(primary);
      }
    } catch (error) {
      console.error('Failed to load contact loans:', error);
    } finally {
      setLoadingLoans(false);
    }
  };

  // Create new pre-qual loan
  const handleCreatePreQual = async () => {
    try {
      const newLoan = await contactLoanService.createLoan({
        contactId: contact.id,
        loanType: 'pre_qual',
        loanPurpose: loanDetails.loanPurpose,
        propertyType: loanDetails.propertyType,
        loanAmount: loanDetails.estimatedLoanAmount,
        propertyValue: loanDetails.estimatedPropertyValue,
        status: 'new',
        isPrimary: contactLoans.length === 0
      });
      setContactLoans(prev => [newLoan, ...prev]);
      setSelectedLoan(newLoan);
      setShowNewPreQualForm(false);
      toast.success('Pre-qualification created');
      setHasChanges(true);
    } catch (error) {
      console.error('Failed to create pre-qual:', error);
      toast.error('Failed to create pre-qualification');
    }
  };

  // Search Encompass loans
  const handleSearchEncompass = async () => {
    setSearchingEncompass(true);
    try {
      const results = await contactLoanService.getAutoSuggestedLoans({
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email
      });
      setEncompassSearchResults(results);
    } catch (error) {
      console.error('Failed to search Encompass:', error);
      toast.error('Failed to search Encompass loans');
    } finally {
      setSearchingEncompass(false);
    }
  };

  // Link Encompass loan
  const handleLinkEncompassLoan = async (encompassLoan: EncompassLoanResult) => {
    try {
      // Check if already linked
      const isLinked = await contactLoanService.isEncompassLoanLinked(contact.id, encompassLoan.encompassLoanId);
      if (isLinked) {
        toast.error('This loan is already linked to this contact');
        return;
      }

      const linkedLoan = await contactLoanService.linkEncompassLoan(
        contact.id,
        encompassLoan,
        contactLoans.length === 0
      );
      setContactLoans(prev => [linkedLoan, ...prev]);
      setSelectedLoan(linkedLoan);
      setShowEncompassSearch(false);
      setEncompassSearchResults([]);
      toast.success('Encompass loan linked');
      setHasChanges(true);
    } catch (error) {
      console.error('Failed to link Encompass loan:', error);
      toast.error('Failed to link Encompass loan');
    }
  };

  // Update selected loan
  const handleUpdateSelectedLoan = async (field: keyof ContactLoan, value: unknown) => {
    if (!selectedLoan) {
return;
}

    try {
      const updated = await contactLoanService.updateLoan(selectedLoan.id, {
        [field]: value
      } as Record<string, unknown>);
      setSelectedLoan(updated);
      setContactLoans(prev => prev.map(l => l.id === updated.id ? updated : l));
      setHasChanges(true);
    } catch (error) {
      console.error('Failed to update loan:', error);
    }
  };

  // Delete a loan
  const handleDeleteLoan = async (loanId: string) => {
    try {
      await contactLoanService.deleteLoan(loanId);
      setContactLoans(prev => {
        const remaining = prev.filter(l => l.id !== loanId);
        if (selectedLoan?.id === loanId) {
          setSelectedLoan(remaining[0] || null);
        }
        return remaining;
      });
      toast.success('Loan removed');
      setHasChanges(true);
    } catch (error) {
      console.error('Failed to delete loan:', error);
      toast.error('Failed to remove loan');
    }
  };

  // Set loan as primary
  const handleSetPrimaryLoan = async (loanId: string) => {
    try {
      const updated = await contactLoanService.setPrimaryLoan(loanId);
      setContactLoans(prev => prev.map(l => ({
        ...l,
        isPrimary: l.id === loanId
      })));
      if (selectedLoan?.id === loanId) {
        setSelectedLoan(updated);
      }
      toast.success('Primary loan updated');
    } catch (error) {
      console.error('Failed to set primary loan:', error);
      toast.error('Failed to update primary loan');
    }
  };

  // Load linked co-borrower contact details
  const loadLinkedCoBorrower = async (contactId: string) => {
    try {
      const linkedContact = await simpleContactService.getById(contactId);
      if (linkedContact) {
        setLinkedCoBorrowerContact(linkedContact);
      }
    } catch (error) {
      console.error('Failed to load linked co-borrower:', error);
    }
  };

  // Search contacts for linking
  const searchContacts = async (query: string) => {
    if (!query.trim()) {
      setContactSearchResults([]);
      return;
    }

    setSearchingContacts(true);
    try {
      const results = await simpleContactService.search(query);
      // Filter out the current contact
      setContactSearchResults(results.filter(c => c.id !== contact.id));
    } catch (error) {
      console.error('Failed to search contacts:', error);
      setContactSearchResults([]);
    } finally {
      setSearchingContacts(false);
    }
  };

  // Handle co-borrower field changes
  const handleCoBorrowerChange = (field: keyof CoBorrowerData, value: string | undefined) => {
    setCoBorrower(prev => prev ? { ...prev, [field]: value } : { [field]: value });
    setHasChanges(true);
  };

  // Add new co-borrower (inline)
  const handleAddCoBorrower = () => {
    setCoBorrower({ firstName: '', lastName: '' });
    setShowCoBorrowerForm(true);
    setHasChanges(true);
  };

  // Remove co-borrower
  const handleRemoveCoBorrower = () => {
    setCoBorrower(null);
    setLinkedCoBorrowerContact(null);
    setShowCoBorrowerForm(false);
    setHasChanges(true);
  };

  // Link existing contact as co-borrower
  const handleLinkContact = (linkedContact: SimpleContact) => {
    setCoBorrower({
      firstName: linkedContact.firstName,
      lastName: linkedContact.lastName,
      email: linkedContact.email,
      mobilePhone: linkedContact.mobilePhone,
      linkedContactId: linkedContact.id
    });
    setLinkedCoBorrowerContact(linkedContact);
    setShowCoBorrowerForm(true);
    setShowContactPicker(false);
    setContactSearchQuery('');
    setContactSearchResults([]);
    setHasChanges(true);
  };

  // Create new contact for co-borrower
  const handleCreateCoBorrowerContact = async () => {
    if (!coBorrower?.firstName || !coBorrower?.lastName) {
      toast.error('Co-borrower first and last name are required');
      return;
    }

    try {
      const newContact = await simpleContactService.create({
        firstName: coBorrower.firstName,
        lastName: coBorrower.lastName,
        email: coBorrower.email,
        mobilePhone: coBorrower.mobilePhone,
        contactType: 'borrower'
      });

      // Link the new contact
      setCoBorrower(prev => prev ? { ...prev, linkedContactId: newContact.id } : null);
      setLinkedCoBorrowerContact(newContact);
      toast.success('Co-borrower contact created and linked');
      setHasChanges(true);
    } catch (error) {
      console.error('Failed to create co-borrower contact:', error);
      toast.error('Failed to create co-borrower contact');
    }
  };

  const handleInputChange = (field: keyof ContactFormData, value: string | boolean | undefined) => {
    setContactData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleLoanChange = (field: keyof LoanDetails, value: string | number | undefined) => {
    setLoanDetails(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!contactData.firstName || !contactData.lastName) {
      toast.error('First name and last name are required');
      return;
    }

    setLoading(true);
    try {
      // Map form data to SimpleContact update
      const updateData: Partial<SimpleContact> = {
        title: contactData.title,
        firstName: contactData.firstName,
        lastName: contactData.lastName,
        middleName: contactData.middleName,
        preferredName: contactData.preferredName,
        suffix: contactData.suffix,
        email: contactData.email,
        workEmail: contactData.workEmail,
        mobilePhone: contactData.mobilePhone,
        homePhone: contactData.homePhone,
        workPhone: contactData.workPhone,
        preferredContactMethod: contactData.preferredContactMethod,
        linkedinUrl: contactData.linkedinUrl,
        website: contactData.website,
        mailingStreet: contactData.mailingStreet,
        mailingUnit: contactData.mailingUnit,
        mailingCity: contactData.mailingCity,
        mailingState: contactData.mailingState,
        mailingZip: contactData.mailingZip,
        mailingCountry: contactData.mailingCountry,
        source: contactData.originalSource,
        assignedLoanOfficerId: contactData.assignedLoanOfficerId,
        optInMarketing: contactData.optInMarketing,
        optInSms: contactData.optInSms,
        notes: contactData.notes,
        // Co-borrower / spouse info
        spouseInfo: coBorrower ? {
          title: coBorrower.title,
          firstName: coBorrower.firstName,
          lastName: coBorrower.lastName,
          suffix: coBorrower.suffix,
          email: coBorrower.email,
          mobilePhone: coBorrower.mobilePhone,
          linkedContactId: coBorrower.linkedContactId
        } : undefined
      };

      await simpleContactService.update(contact.id, updateData);

      // Update lead if loan details exist
      if (loanDetails.loanPurpose || loanDetails.estimatedLoanAmount) {
        const { data: existingLead } = await supabase
          .from('leads')
          .select('id')
          .eq('contact_id', contact.id)
          .limit(1)
          .maybeSingle();

        if (existingLead) {
          await supabase
            .from('leads')
            .update({
              loan_purpose: loanDetails.loanPurpose,
              property_type: loanDetails.propertyType,
              estimated_loan_amount: loanDetails.estimatedLoanAmount,
              estimated_property_value: loanDetails.estimatedPropertyValue
            })
            .eq('id', existingLead.id);
        }
      }

      setHasChanges(false);
      toast.success('Contact saved successfully');
      onSaved?.(contact.id);
    } catch (error) {
      console.error('Failed to save contact:', error);
      toast.error('Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await simpleContactService.softDelete(contact.id);
      toast.success('Contact deleted');
      onDeleted?.(contact.id);
      onBack();
    } catch (error) {
      console.error('Failed to delete contact:', error);
      toast.error('Failed to delete contact');
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  const formatCurrency = (value: number | undefined) => {
    if (!value) {
return '';
}
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  const parseCurrency = (value: string): number | undefined => {
    const num = parseFloat(value.replace(/[^0-9.-]+/g, ''));
    return isNaN(num) ? undefined : num;
  };

  const selectedLoanView = selectedLoan ? describeContactLoan(selectedLoan) : null;

  return (
    <div className="flex flex-col h-full">
      {/* Header with contact summary */}
      <div className="border-b bg-background sticky top-0 z-10">
        {/* Top bar with back button and actions */}
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Contacts
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={loading}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={loading || !hasChanges}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>
        </div>

        {/* Contact name, summary, and assigned LO */}
        <div className="px-6 pb-4">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-semibold">
                {contactData.firstName} {contactData.lastName}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {contactData.email || 'No email'}
                {contactData.mobilePhone && ` • ${contactData.mobilePhone}`}
                {contactData.mailingCity && contactData.mailingState && ` • ${contactData.mailingCity}, ${contactData.mailingState}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">Assigned LO:</Label>
              <LoanOfficerSelect
                value={contactData.assignedLoanOfficerId || ''}
                onChange={(value) => handleInputChange('assignedLoanOfficerId', value)}
                placeholder="Select loan officer"
                label=""
                className="w-[200px]"
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
            <TabsList className="w-auto">
              <TabsTrigger value="contact" className="gap-2">
                <User className="h-4 w-4" />
                Contact Info
              </TabsTrigger>
              <TabsTrigger value="loan" className="gap-2">
                <Home className="h-4 w-4" />
                Loan Details
              </TabsTrigger>
              <TabsTrigger value="documents" className="gap-2">
                <FileText className="h-4 w-4" />
                Documents
              </TabsTrigger>
              <TabsTrigger value="pre-approvals" className="gap-2">
                <FileCheck className="h-4 w-4" />
                Pre-Approvals
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
          {/* Contact Information Tab */}
          <TabsContent value="contact" className="m-0 p-6">
            <Card>
              <CardContent className="p-6 space-y-6">
                {/* Name Row */}
                <div>
                  <h3 className="text-sm font-medium mb-3">Personal Information</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs">Title</Label>
                      <Select
                        value={contactData.title || ''}
                        onValueChange={(value) => handleInputChange('title', value)}
                      >
                        <SelectTrigger className="h-9 mt-1">
                          <SelectValue placeholder="Title" />
                        </SelectTrigger>
                        <SelectContent>
                          {TITLE_OPTIONS.map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">First Name *</Label>
                      <Input
                        className="h-9 mt-1"
                        value={contactData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        placeholder="First"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Last Name *</Label>
                      <Input
                        className="h-9 mt-1"
                        value={contactData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        placeholder="Last"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Suffix</Label>
                      <Select
                        value={contactData.suffix || ''}
                        onValueChange={(value) => handleInputChange('suffix', value)}
                      >
                        <SelectTrigger className="h-9 mt-1">
                          <SelectValue placeholder="Suffix" />
                        </SelectTrigger>
                        <SelectContent>
                          {SUFFIX_OPTIONS.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Co-Borrower Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      Co-Borrower
                    </h3>
                    {!showCoBorrowerForm && (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleAddCoBorrower}
                          className="h-7 text-xs"
                        >
                          <UserPlus className="h-3 w-3 mr-1" />
                          Add Co-Borrower
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowContactPicker(true)}
                          className="h-7 text-xs"
                        >
                          <Link2 className="h-3 w-3 mr-1" />
                          Link Existing
                        </Button>
                      </div>
                    )}
                    {showCoBorrowerForm && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveCoBorrower}
                        className="h-7 text-xs text-destructive hover:text-destructive"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>

                  {/* Contact Picker */}
                  {showContactPicker && (
                    <div className="mb-4 p-3 border rounded-md bg-muted/30">
                      <div className="flex items-center gap-2 mb-2">
                        <Input
                          placeholder="Search contacts by name or email..."
                          value={contactSearchQuery}
                          onChange={(e) => {
                            setContactSearchQuery(e.target.value);
                            searchContacts(e.target.value);
                          }}
                          className="h-8"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowContactPicker(false);
                            setContactSearchQuery('');
                            setContactSearchResults([]);
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      {searchingContacts && (
                        <div className="flex items-center justify-center py-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      )}
                      {contactSearchResults.length > 0 && (
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {contactSearchResults.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => handleLinkContact(c)}
                              className="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm"
                            >
                              <span className="font-medium">{c.firstName} {c.lastName}</span>
                              {c.email && <span className="text-muted-foreground ml-2">{c.email}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                      {contactSearchQuery && !searchingContacts && contactSearchResults.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-2">No contacts found</p>
                      )}
                    </div>
                  )}

                  {/* Co-Borrower Form */}
                  {showCoBorrowerForm && coBorrower && (
                    <div className="space-y-3 p-3 border rounded-md bg-muted/30">
                      {/* Linked Contact Badge */}
                      {linkedCoBorrowerContact && (
                        <div className="flex items-center justify-between p-2 bg-primary/10 rounded-md mb-3">
                          <div className="flex items-center gap-2">
                            <Link2 className="h-4 w-4 text-primary" />
                            <span className="text-sm">
                              Linked to: <span className="font-medium">{linkedCoBorrowerContact.firstName} {linkedCoBorrowerContact.lastName}</span>
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              // View linked contact (could navigate to their detail page)
                              window.open(`/contacts/${linkedCoBorrowerContact.id}`, '_blank');
                            }}
                            className="h-7 text-xs"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View
                          </Button>
                        </div>
                      )}

                      {/* Name Row */}
                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Title</Label>
                          <Select
                            value={coBorrower.title || ''}
                            onValueChange={(value) => handleCoBorrowerChange('title', value)}
                            disabled={!!linkedCoBorrowerContact}
                          >
                            <SelectTrigger className="h-8 mt-1">
                              <SelectValue placeholder="Title" />
                            </SelectTrigger>
                            <SelectContent>
                              {TITLE_OPTIONS.map(t => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">First Name</Label>
                          <Input
                            className="h-8 mt-1"
                            value={coBorrower.firstName || ''}
                            onChange={(e) => handleCoBorrowerChange('firstName', e.target.value)}
                            placeholder="First"
                            disabled={!!linkedCoBorrowerContact}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Last Name</Label>
                          <Input
                            className="h-8 mt-1"
                            value={coBorrower.lastName || ''}
                            onChange={(e) => handleCoBorrowerChange('lastName', e.target.value)}
                            placeholder="Last"
                            disabled={!!linkedCoBorrowerContact}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Suffix</Label>
                          <Select
                            value={coBorrower.suffix || ''}
                            onValueChange={(value) => handleCoBorrowerChange('suffix', value)}
                            disabled={!!linkedCoBorrowerContact}
                          >
                            <SelectTrigger className="h-8 mt-1">
                              <SelectValue placeholder="Suffix" />
                            </SelectTrigger>
                            <SelectContent>
                              {SUFFIX_OPTIONS.map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Contact Info Row */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Email</Label>
                          <Input
                            className="h-8 mt-1"
                            type="email"
                            value={coBorrower.email || ''}
                            onChange={(e) => handleCoBorrowerChange('email', e.target.value)}
                            placeholder="email@example.com"
                            disabled={!!linkedCoBorrowerContact}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Mobile Phone</Label>
                          <Input
                            className="h-8 mt-1"
                            type="tel"
                            value={coBorrower.mobilePhone || ''}
                            onChange={(e) => handleCoBorrowerChange('mobilePhone', e.target.value)}
                            placeholder="(555) 123-4567"
                            disabled={!!linkedCoBorrowerContact}
                          />
                        </div>
                      </div>

                      {/* Create separate contact option */}
                      {!linkedCoBorrowerContact && coBorrower.firstName && coBorrower.lastName && (
                        <div className="pt-2 border-t">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleCreateCoBorrowerContact}
                            className="h-7 text-xs"
                          >
                            <UserPlus className="h-3 w-3 mr-1" />
                            Create as Separate Contact
                          </Button>
                          <p className="text-xs text-muted-foreground mt-1">
                            This will create a new contact record for the co-borrower
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Empty state */}
                  {!showCoBorrowerForm && !showContactPicker && (
                    <p className="text-sm text-muted-foreground">No co-borrower added</p>
                  )}
                </div>

                {/* Contact Methods */}
                <div>
                  <h3 className="text-sm font-medium mb-3">Contact Methods</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs flex items-center gap-1">
                        <Mail className="h-3 w-3" /> Email
                      </Label>
                      <Input
                        className="h-9 mt-1"
                        type="email"
                        value={contactData.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <Label className="text-xs flex items-center gap-1">
                        <Phone className="h-3 w-3" /> Mobile Phone
                      </Label>
                      <Input
                        className="h-9 mt-1"
                        type="tel"
                        value={contactData.mobilePhone || ''}
                        onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-sm font-medium mb-3 flex items-center gap-1">
                    <MapPin className="h-4 w-4" /> Mailing Address
                  </h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-2">
                        <Label className="text-xs">Street Address</Label>
                        <Input
                          className="h-9 mt-1"
                          value={contactData.mailingStreet || ''}
                          onChange={(e) => handleInputChange('mailingStreet', e.target.value)}
                          placeholder="Street Address"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Unit/Apt</Label>
                        <Input
                          className="h-9 mt-1"
                          value={contactData.mailingUnit || ''}
                          onChange={(e) => handleInputChange('mailingUnit', e.target.value)}
                          placeholder="Unit/Apt"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">City</Label>
                        <Input
                          className="h-9 mt-1"
                          value={contactData.mailingCity || ''}
                          onChange={(e) => handleInputChange('mailingCity', e.target.value)}
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">State</Label>
                        <Input
                          className="h-9 mt-1"
                          value={contactData.mailingState || ''}
                          onChange={(e) => handleInputChange('mailingState', e.target.value)}
                          placeholder="State"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">ZIP Code</Label>
                        <Input
                          className="h-9 mt-1"
                          value={contactData.mailingZip || ''}
                          onChange={(e) => handleInputChange('mailingZip', e.target.value)}
                          placeholder="ZIP"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lead Source */}
                <div>
                  <h3 className="text-sm font-medium mb-3">Lead Source</h3>
                  <div className="w-1/2">
                    <Label className="text-xs">Original Source</Label>
                    <Select
                      value={contactData.originalSource || ''}
                      onValueChange={(value) => handleInputChange('originalSource', value)}
                    >
                      <SelectTrigger className="h-9 mt-1">
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        {LEAD_SOURCES.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Opt-ins */}
                <div>
                  <h3 className="text-sm font-medium mb-3">Communication Preferences</h3>
                  <div className="flex gap-6">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="optInMarketing"
                        checked={contactData.optInMarketing}
                        onCheckedChange={(checked) => handleInputChange('optInMarketing', checked as boolean)}
                      />
                      <Label htmlFor="optInMarketing" className="text-sm">Marketing Opt-In</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="optInSms"
                        checked={contactData.optInSms}
                        onCheckedChange={(checked) => handleInputChange('optInSms', checked as boolean)}
                      />
                      <Label htmlFor="optInSms" className="text-sm">SMS Opt-In</Label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Loan Details Tab */}
          <TabsContent value="loan" className="m-0 p-6">
            <div className="space-y-6">
              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewPreQualForm(true)}
                  className="h-8"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New Pre-Qual
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowEncompassSearch(true);
                    handleSearchEncompass();
                  }}
                  className="h-8"
                >
                  <Link2 className="h-4 w-4 mr-1" />
                  Link Encompass Loan
                </Button>
              </div>

              {/* Encompass Loan Search Modal */}
              {showEncompassSearch && (
                <Card className="border-primary/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium flex items-center gap-2">
                        <Search className="h-4 w-4" />
                        Link Encompass Loan
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowEncompassSearch(false);
                          setEncompassSearchResults([]);
                        }}
                        className="h-7 w-7 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      Showing loans matching: {contact.firstName} {contact.lastName}
                      {contact.email && ` / ${contact.email}`}
                    </p>
                    {searchingEncompass ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin" />
                      </div>
                    ) : encompassSearchResults.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No matching Encompass loans found
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {encompassSearchResults.map((loan) => (
                          <button
                            key={loan.encompassLoanId}
                            type="button"
                            onClick={() => handleLinkEncompassLoan(loan)}
                            className="w-full text-left p-3 rounded-md border hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="text-sm font-medium">
                                  {loan.loanPurpose || 'Loan'} - {formatCurrency(loan.loanAmount || 0)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {loan.propertyAddress && `${loan.propertyAddress}, `}
                                  {loan.propertyCity}, {loan.propertyState}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {loan.borrowerFirstName} {loan.borrowerLastName}
                                </p>
                              </div>
                              <div className="text-right">
                                {loan.loanNumber && (
                                  <Badge variant="outline" className="text-xs">
                                    #{loan.loanNumber}
                                  </Badge>
                                )}
                                {loan.status && (
                                  <p className="text-xs text-muted-foreground mt-1">{loan.status}</p>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Loan List */}
              {contactLoans.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-medium mb-3">Your Loans ({contactLoans.length})</h3>
                    <div className="space-y-2">
                      {contactLoans.map((loan) => ({ loan, view: describeContactLoan(loan) })).map(({ loan, view }) => (
                        <button
                          key={loan.id}
                          type="button"
                          onClick={() => setSelectedLoan(loan)}
                          className={`w-full text-left p-3 rounded-md border transition-colors ${
                            selectedLoan?.id === loan.id
                              ? 'border-primary bg-primary/5'
                              : 'hover:bg-muted/50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`mt-1 w-3 h-3 rounded-full ${
                                selectedLoan?.id === loan.id
                                  ? 'bg-primary'
                                  : 'border-2 border-muted-foreground/30'
                              }`} />
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium">
                                    {view.title} - {formatCurrency(loan.loanAmount || 0)}
                                  </p>
                                  {loan.isPrimary && (
                                    <Badge variant="secondary" className="text-[10px] h-4">Primary</Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {loan.propertyAddress ? `${loan.propertyAddress}, ` : ''}
                                  {loan.propertyCity && loan.propertyState
                                    ? `${loan.propertyCity}, ${loan.propertyState}`
                                    : 'No property address'}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              {view.kind === 'encompass' ? (
                                <Badge variant="outline" className="text-xs">
                                  <Building className="h-3 w-3 mr-1" />
                                  {view.loanNumber || 'Encompass'}
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">{view.badgeLabel}</Badge>
                              )}
                              <p className="text-xs text-muted-foreground mt-1 capitalize">
                                {view.statusLabel}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Selected Loan Details */}
              {selectedLoan && selectedLoanView ? (
                <Card>
                  <CardContent className="p-6 space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">Loan Details</h3>
                      <div className="flex items-center gap-2">
                        {!selectedLoan.isPrimary && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSetPrimaryLoan(selectedLoan.id)}
                            className="h-7 text-xs"
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Set as Primary
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteLoan(selectedLoan.id)}
                          className="h-7 text-xs text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>

                    {/* Loan Information */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Loan Purpose</Label>
                        <Select
                          value={selectedLoanView.purposeValue}
                          onValueChange={(value) => handleUpdateSelectedLoan('loanPurpose', value)}
                          disabled={!selectedLoanView.editable}
                        >
                          <SelectTrigger className="h-9 mt-1">
                            <SelectValue placeholder="Select purpose" />
                          </SelectTrigger>
                          <SelectContent>
                            {LOAN_PURPOSES.map(p => (
                              <SelectItem key={p} value={p}>{p}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Property Type</Label>
                        <Select
                          value={selectedLoan.propertyType || ''}
                          onValueChange={(value) => handleUpdateSelectedLoan('propertyType', value)}
                          disabled={!selectedLoanView.editable}
                        >
                          <SelectTrigger className="h-9 mt-1">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {PROPERTY_TYPES.map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Financial Details */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs flex items-center gap-1">
                          <DollarSign className="h-3 w-3" /> Loan Amount
                        </Label>
                        <Input
                          className="h-9 mt-1"
                          value={formatCurrency(selectedLoan.loanAmount)}
                          onChange={(e) => handleUpdateSelectedLoan('loanAmount', parseCurrency(e.target.value))}
                          placeholder="$0"
                          disabled={!selectedLoanView.editable}
                        />
                      </div>
                      <div>
                        <Label className="text-xs flex items-center gap-1">
                          <DollarSign className="h-3 w-3" /> Property Value
                        </Label>
                        <Input
                          className="h-9 mt-1"
                          value={formatCurrency(selectedLoan.propertyValue)}
                          onChange={(e) => handleUpdateSelectedLoan('propertyValue', parseCurrency(e.target.value))}
                          placeholder="$0"
                          disabled={!selectedLoanView.editable}
                        />
                      </div>
                    </div>

                    {/* System Information */}
                    {selectedLoanView.showEncompassInfo && (
                      <div>
                        <h4 className="text-xs font-medium mb-2">Encompass Information</h4>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            <Building className="h-3 w-3 mr-1" />
                            Loan ID: {selectedLoan.encompassLoanId}
                          </Badge>
                          {selectedLoanView.loanNumber && (
                            <Badge variant="outline" className="text-xs">
                              #{selectedLoanView.loanNumber}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div>
                      <Label className="text-xs">Notes</Label>
                      <Textarea
                        className="min-h-[100px] mt-1"
                        value={selectedLoan.notes || ''}
                        onChange={(e) => handleUpdateSelectedLoan('notes', e.target.value)}
                        placeholder="Add notes about this loan..."
                      />
                    </div>
                  </CardContent>
                </Card>
              ) : !showNewPreQualForm && !showEncompassSearch && contactLoans.length === 0 ? (
                <Card>
                  <CardContent className="p-6 text-center">
                    <Home className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
                    <h3 className="text-sm font-medium mb-1">No Loans Yet</h3>
                    <p className="text-xs text-muted-foreground mb-4">
                      Create a new pre-qualification or link an existing Encompass loan
                    </p>
                  </CardContent>
                </Card>
              ) : null}

              {/* New Pre-Qual Form */}
              {showNewPreQualForm && (
                <Card className="border-primary/50">
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">New Pre-Qualification</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowNewPreQualForm(false)}
                        className="h-7 w-7 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Loan Purpose</Label>
                        <Select
                          value={loanDetails.loanPurpose || ''}
                          onValueChange={(value) => handleLoanChange('loanPurpose', value)}
                        >
                          <SelectTrigger className="h-9 mt-1">
                            <SelectValue placeholder="Select purpose" />
                          </SelectTrigger>
                          <SelectContent>
                            {LOAN_PURPOSES.map(p => (
                              <SelectItem key={p} value={p}>{p}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Property Type</Label>
                        <Select
                          value={loanDetails.propertyType || ''}
                          onValueChange={(value) => handleLoanChange('propertyType', value)}
                        >
                          <SelectTrigger className="h-9 mt-1">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {PROPERTY_TYPES.map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Loan Amount</Label>
                        <Input
                          className="h-9 mt-1"
                          value={formatCurrency(loanDetails.estimatedLoanAmount)}
                          onChange={(e) => handleLoanChange('estimatedLoanAmount', parseCurrency(e.target.value))}
                          placeholder="$0"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Property Value</Label>
                        <Input
                          className="h-9 mt-1"
                          value={formatCurrency(loanDetails.estimatedPropertyValue)}
                          onChange={(e) => handleLoanChange('estimatedPropertyValue', parseCurrency(e.target.value))}
                          placeholder="$0"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowNewPreQualForm(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleCreatePreQual}
                      >
                        Create Pre-Qual
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="m-0 h-[calc(100vh-220px)]">
            <DocumentsPanel
              contactId={contact.id}
              encompassLoanId={loanDetails.encompassLoanId}
            />
          </TabsContent>

          {/* Pre-Approvals Tab */}
          <TabsContent value="pre-approvals" className="m-0 p-6">
            <ContactPreApprovalsPanel
              contactId={contact.id}
              contact={contact}
              onCreatePreApproval={onCreatePreApproval || (() => {})}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {contactData.firstName} {contactData.lastName}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ContactDetailPage;
